import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Link2, Copy, Check, X, Zap } from "lucide-react";

/**
 * Front-desk / staff helper: click to generate a Razorpay UPI / hosted payment
 * link for an unpaid invoice, then share it with the parent (WhatsApp/SMS).
 * On success the invoice is auto-marked paid via webhook.
 */
export default function CollectPaymentButton({ invoiceId, onCollected }) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/razorpay/payment-link", {
        invoice_id: invoiceId,
      });
      setLink(data);
      toast.success(`Payment link created · ₹${data.amount_inr}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    if (!link?.short_url) return;
    await navigator.clipboard.writeText(link.short_url);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const openWhatsApp = () => {
    if (!link?.short_url) return;
    const msg = `Hi, here's the payment link for invoice ${link.invoice_number}: ${link.short_url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (link) {
    return (
      <div className="border border-brand-orange/40 bg-brand-orange/10 p-3" data-testid={`collect-link-${invoiceId}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest brand-orange">Payment link ready</div>
          <button onClick={() => setLink(null)} className="text-white/40 hover:text-white"><X size={14} /></button>
        </div>
        <div className="flex items-center gap-2 bg-black/40 border border-white/12 px-2 py-1.5">
          <code className="flex-1 font-mono text-[11px] text-white/80 truncate">{link.short_url}</code>
          <button onClick={copy} data-testid={`collect-copy-${invoiceId}`} className="text-white/60 hover:text-white">
            {copied ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
          </button>
        </div>
        <div className="flex gap-2 mt-2">
          <button onClick={openWhatsApp} data-testid={`collect-wa-${invoiceId}`}
            className="text-[10px] font-mono uppercase tracking-widest border border-white/20 px-2 py-1 hover:bg-white/6 flex items-center gap-1">
            <Zap size={11} /> Share on WhatsApp
          </button>
          <a href={link.short_url} target="_blank" rel="noreferrer"
            className="text-[10px] font-mono uppercase tracking-widest brand-blue hover:underline px-2 py-1">
            Open
          </a>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={create}
      disabled={busy}
      data-testid={`collect-btn-${invoiceId}`}
      className="text-[10px] font-mono uppercase tracking-widest border border-brand-orange text-brand-orange px-2 py-1 hover:bg-brand-orange hover:text-black transition flex items-center gap-1"
    >
      <Link2 size={11} /> {busy ? "…" : "Collect Payment"}
    </button>
  );
}
